// src/components/dashboard/client/ClientDashboard.js - VERSION ORIGINALE RESTAURÉE
import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion, Timestamp, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { getAuth } from 'firebase/auth';
import { 
  Calendar, 
  Bell, 
  FileText, 
  Settings, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  Download,
  Phone,
  X,
  Send,
  Paperclip,
  Users,
  Trash2,
  Eye,
  EyeOff,
  MessageSquare,
  Folder,
  ArrowLeft
} from 'lucide-react';
import NewClientCalendar from './NewClientCalendar';
import useUnreadMessagesCount from '../../../hooks/useUnreadMessagesCount';
import eventBus from '../../../utils/eventBus';

// Couleurs pour les différents rôles
const ROLE_COLORS = {
  'Super Admin': { bubble: 'bg-purple-100 border-purple-300', avatar: 'bg-purple-500' },
  'Chef de Projet': { bubble: 'bg-blue-100 border-blue-300', avatar: 'bg-blue-500' },
  'Chargé d\'Affaire': { bubble: 'bg-green-100 border-green-300', avatar: 'bg-green-500' },
  'Client': { bubble: 'bg-yellow-100 border-yellow-300', avatar: 'bg-yellow-500' },
  'Développeur': { bubble: 'bg-red-100 border-red-300', avatar: 'bg-red-500' },
  'Utilisateur': { bubble: 'bg-gray-100 border-gray-300', avatar: 'bg-gray-500' }
};

const getColorsByRole = (role) => ROLE_COLORS[role] || ROLE_COLORS['Utilisateur'];

// PRINCIPAL: ClientDashboard pour un projet spécifique
const ClientDashboard = () => {
  const { dossierId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [dossierData, setDossierData] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMultiProject, setIsMultiProject] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPlanning, setShowPlanning] = useState(false);
  
  // États pour la messagerie
  const [discussions, setDiscussions] = useState([]);
  const [userRoles, setUserRoles] = useState({});
  const [discussionData, setDiscussionData] = useState(null);

  const auth = getAuth();
  const { count: messagesCount } = useUnreadMessagesCount(auth.currentUser?.uid, auth.currentUser?.email);

  // Vérifier si on est sur une sous-route
  const isOnSubRoute = location.pathname !== `/client/${dossierId || ''}` && dossierId;

  // CORRIGÉ: Charger tous les projets de l'utilisateur avec logique de redirection claire
  useEffect(() => {
    const loadUserProjects = async () => {
      if (!auth.currentUser) {
        console.log("❌ DEBUG: Pas d'utilisateur connecté");
        return;
      }
      
      try {
        console.log("🔍 DEBUG: Chargement des projets de l'utilisateur:", auth.currentUser.uid);
        
        // Récupérer les informations utilisateur
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("📊 DEBUG: Données utilisateur complètes:", userData);
          
          const dossierIds = userData.dossierIds || [userData.dossierId].filter(Boolean);
          console.log("📂 DEBUG: dossierIds trouvés:", dossierIds);
          
          if (dossierIds.length > 0) {
            console.log(`🔄 DEBUG: Chargement de ${dossierIds.length} projet(s)...`);
            
            // Charger tous les projets - CORRIGÉ: collection 'dossiers'
            const projectPromises = dossierIds.map(id => {
              console.log(`📥 DEBUG: Chargement dossier ${id}`);
              return getDoc(doc(db, "dossiers", id));
            });
            const projectDocs = await Promise.all(projectPromises);
            
            const projects = projectDocs
              .filter(doc => {
                const exists = doc.exists();
                console.log(`📄 DEBUG: Dossier ${doc.id} existe: ${exists}`);
                return exists;
              })
              .map(doc => {
                const data = { id: doc.id, ...doc.data() };
                console.log(`📋 DEBUG: Dossier chargé:`, { id: data.id, title: data.titre || data.dossierTitre });
                return data;
              })
              .sort((a, b) => (b.createdAt?.toDate?.() || new Date()) - (a.createdAt?.toDate?.() || new Date()));
            
            console.log(`✅ DEBUG: ${projects.length} dossier(s) chargé(s):`, projects.map(p => ({ id: p.id, title: p.titre || p.dossierTitre })));
            
            setUserProjects(projects);
            setIsMultiProject(projects.length > 1);
            
            console.log(`🎯 DEBUG: isMultiProject = ${projects.length > 1} (car ${projects.length} dossiers)`);
            
            // CORRIGÉ: Logique de redirection claire
            if (!dossierId) {
              // Pas de dossierId dans l'URL
              if (projects.length === 1) {
                // Un seul projet → Rediriger vers ce projet
                console.log("🔀 DEBUG: Un seul dossier sans dossierId → Redirection vers", projects[0].id);
                navigate(`/client/${projects[0].id}`, { replace: true });
                return;
              } else if (projects.length > 1) {
                // Multi-projets → Rediriger vers MultiDashboardClient (/client)
                console.log("🔀 DEBUG: Multi-dossiers sans dossierId → Redirection vers /client");
                navigate('/client', { replace: true });
                return;
              }
            } else {
              // dossierId spécifique → Vérifier qu'il existe et continuer
              const projectExists = projects.some(p => p.id === dossierId);
              if (!projectExists) {
                console.log("❌ DEBUG: Dossier", dossierId, "n'existe pas → Redirection");
                if (projects.length === 1) {
                  navigate(`/client/${projects[0].id}`, { replace: true });
                } else {
                  navigate('/client', { replace: true });
                }
                return;
              }
            }
          } else {
            console.log("❌ DEBUG: Aucun dossierIds trouvé");
          }
        } else {
          console.log("❌ DEBUG: Document utilisateur n'existe pas");
        }
      } catch (error) {
        console.error("❌ DEBUG: Erreur lors du chargement des dossiers:", error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProjects();
  }, [auth.currentUser, dossierId, navigate]);

  // CORRIGÉ: Fonction pour retourner à la sélection multi-projets
  const handleBackToProjects = () => {
    console.log("🔄 DEBUG: Retour vers sélection multi-dossiers");
    console.log("🔄 DEBUG: URL de destination:", `/client`);
    navigate('/client');
  };

  // Fonction pour empêcher le scroll quand on appuie sur espace dans le modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((showNotifications || showPlanning) && e.code === 'Space') {
        const activeElement = document.activeElement;
        if (activeElement && (activeElement.tagName === 'TEXTAREA' || activeElement.tagName === 'INPUT')) {
          e.stopPropagation();
        }
      }
    };

    if (showNotifications || showPlanning) {
      document.addEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = 'unset';
    };
  }, [showNotifications, showPlanning]);

  // Fonctions utilitaires
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Chargement des rôles utilisateurs
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        console.log("Chargement des rôles utilisateurs...");
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const roles = {};
        
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          roles[doc.id] = {
            role: userData.role || 'Utilisateur',
            email: userData.email,
            displayName: userData.displayName || userData.email
          };
          
          if (userData.email) {
            roles[userData.email] = {
              role: userData.role || 'Utilisateur',
              id: doc.id,
              displayName: userData.displayName || userData.email
            };
          }
        });
        
        console.log("Rôles chargés:", roles);
        setUserRoles(roles);
      } catch (error) {
        console.error('Erreur lors de la récupération des rôles:', error);
      }
    };
    
    fetchUserRoles();
  }, []);

  // Chargement des données du dossier (seulement si dossierId spécifique)
  useEffect(() => {
    const loadDossierData = async () => {
      if (!dossierId) return;
      
      try {
        console.log("Chargement du dossier:", dossierId);
        const dossierDoc = await getDoc(doc(db, "dossiers", dossierId));
        
        if (dossierDoc.exists()) {
          const data = dossierDoc.data();
          const progressGlobal = data.progressGlobal || 0;
          
          data.phases = [
            { 
              id: 1, 
              nom: "Signature du devis & versement de l'acompte", 
              statut: "completed",
              dateFinition: data.createdAt?.toDate?.() || new Date(),
              description: "Finalisation des aspects financiers"
            },
            { 
              id: 2, 
              nom: "Déclaration préalable aux travaux en mairie", 
              statut: progressGlobal >= 25 ? "completed" : progressGlobal >= 12.5 ? "in_progress" : "pending",
              dateFinition: progressGlobal >= 25 ? data.createdAt?.toDate?.() : null,
              dateDebut: progressGlobal >= 12.5 && progressGlobal < 25 ? data.createdAt?.toDate?.() : null,
              description: "Déclaration déposée en mairie obtention de l'autorisation"
            },
            { 
              id: 3, 
              nom: "Autorisation administrative pour le raccordement", 
              statut: progressGlobal >= 37.5 ? "completed" : progressGlobal >= 25 ? "in_progress" : "pending",
              dateFinition: progressGlobal >= 37.5 ? data.createdAt?.toDate?.() : null,
              dateDebut: progressGlobal >= 25 && progressGlobal < 37.5 ? data.createdAt?.toDate?.() : null,
              description: "Demande auprès d'Enedis et validation de la complétude du dossier"
            },
            { 
              id: 4, 
              nom: "Livraison du matériel", 
              statut: progressGlobal >= 50 ? "completed" : progressGlobal >= 37.5 ? "in_progress" : "pending",
              dateFinition: progressGlobal >= 50 ? data.createdAt?.toDate?.() : null,
              dateDebut: progressGlobal >= 37.5 && progressGlobal < 50 ? data.createdAt?.toDate?.() : null,
              description: "Préparation de l'installation"
            },
            { 
              id: 5, 
              nom: "Mise en service", 
              statut: progressGlobal >= 62.5 ? "completed" : progressGlobal >= 50 ? "in_progress" : "pending",
              dateFinition: progressGlobal >= 62.5 ? data.createdAt?.toDate?.() : null,
              dateDebut: progressGlobal >= 50 && progressGlobal < 62.5 ? data.createdAt?.toDate?.() : null,
              description: "Installation des équipements"
            },
            { 
              id: 6, 
              nom: "Démarrage de la vente du surplus", 
              statut: progressGlobal >= 75 ? "completed" : progressGlobal >= 62.5 ? "in_progress" : "pending",
              dateFinition: progressGlobal >= 75 ? data.createdAt?.toDate?.() : null,
              dateDebut: progressGlobal >= 62.5 && progressGlobal < 75 ? data.createdAt?.toDate?.() : null,
              description: "Activation du contrat de rachat"
            },
            { 
              id: 7, 
              nom: "Réception et signature du contrat EDF", 
              statut: progressGlobal >= 87.5 ? "completed" : progressGlobal >= 75 ? "in_progress" : "pending",
              dateFinition: progressGlobal >= 87.5 ? data.createdAt?.toDate?.() : null,
              dateDebut: progressGlobal >= 75 && progressGlobal < 87.5 ? data.createdAt?.toDate?.() : null,
              description: "Contrat signé et envoyé à EDF"
            },
            { 
              id: 8, 
              nom: "Clôture du dossier", 
              statut: progressGlobal >= 100 ? "completed" : progressGlobal >= 87.5 ? "in_progress" : "pending",
              dateFinition: progressGlobal >= 100 ? data.createdAt?.toDate?.() : null,
              dateDebut: progressGlobal >= 87.5 && progressGlobal < 100 ? data.createdAt?.toDate?.() : null,
              description: "Finalisation complète du projet"
            }
          ];
          
          data.etapeActuelle = data.phases.filter(p => p.statut === 'completed').length + 1;
          
          if (!data.puissance) {
            data.puissance = data.raccordement === "autoconsommation" ? "6 kWc" : "9 kWc";
          }
          
          console.log("Dossier chargé:", data);
          setDossierData(data);
        } else {
          console.log("Dossier non trouvé");
          setDossierData(null);
        }
      } catch (error) {
        console.error("Erreur lors du chargement du dossier:", error);
        setDossierData(null);
      }
    };

    if (dossierId) {
      loadDossierData();
    }
  }, [dossierId]);

  // Observer les DISCUSSIONS en temps réel (seulement si dossierId spécifique)
  useEffect(() => {
    if (!dossierId) return;

    console.log("Mise en place du listener pour les discussions, dossierId:", dossierId);

    const discussionsQuery = query(
      collection(db, 'discussions'),
      where('dossierId', '==', dossierId)
    );

    const unsubscribe = onSnapshot(discussionsQuery, (snapshot) => {
      console.log("📥 Nouveau snapshot reçu:", snapshot.docs.length, "discussions");
      
      const discussionsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log("Discussions brutes:", discussionsList);

      discussionsList.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });

      setDiscussions(discussionsList);

      const mainDiscussion = discussionsList.find(d => d.dossierId === dossierId) || discussionsList[0];
      console.log("Discussion principale trouvée:", mainDiscussion);
      
      if (mainDiscussion) {
        console.log("🔄 Mise à jour de la discussion principale");
        setDiscussionData(mainDiscussion);
      }
    }, (error) => {
      console.error("❌ Erreur dans le listener discussions:", error);
    });

    return () => {
      console.log("🔌 Déconnexion du listener discussions");
      unsubscribe();
    };
  }, [dossierId]);

  // Marquer les messages comme lus dans les discussions - VERSION DEBUG
  const markMessagesAsRead = async (discussion) => {
    const auth = getAuth();
    if (!auth.currentUser) {
      console.log('❌ MARK-READ-DEBUG: Pas d\'utilisateur connecté');
      return;
    }

    try {
      console.log('🔄 MARK-READ-DEBUG: *** DÉBUT MARQUAGE COMME LU ***');
      console.log('🔄 MARK-READ-DEBUG: Discussion ID:', discussion.id);
      console.log('🔄 MARK-READ-DEBUG: User ID:', auth.currentUser.uid);
      console.log('🔄 MARK-READ-DEBUG: Nombre de messages:', discussion.messages?.length || 0);
      
      const updates = {};
      let needsUpdate = false;

      // ✅ Marquer la discussion comme lue
      if (!discussion.read) {
        console.log('🔄 MARK-READ-DEBUG: Marquage discussion comme lue');
        updates.read = true;
        needsUpdate = true;
      } else {
        console.log('✅ MARK-READ-DEBUG: Discussion déjà marquée comme lue');
      }

      // ✅ Marquer chaque MESSAGE individuel comme lu
      if (discussion.messages && discussion.messages.length > 0) {
        console.log('🔄 MARK-READ-DEBUG: Analyse des messages...');
        
        let messagesModified = 0;
        const updatedMessages = discussion.messages.map((message, index) => {
          const currentReadBy = message.readBy || [];
          const isAlreadyRead = currentReadBy.includes(auth.currentUser.uid);
          
          console.log(`  📨 MARK-READ-DEBUG: Message ${index + 1}:`);
          console.log(`    - Créé par: ${message.createdBy}`);
          console.log(`    - Texte: "${message.text?.substring(0, 30)}..."`);
          console.log(`    - ReadBy actuel: [${currentReadBy.join(', ')}]`);
          console.log(`    - Déjà lu: ${isAlreadyRead}`);
          
          if (!isAlreadyRead) {
            console.log(`    - 🔥 MODIFICATION: Ajout de ${auth.currentUser.uid} à readBy`);
            messagesModified++;
            return {
              ...message,
              readBy: [...currentReadBy, auth.currentUser.uid]
            };
          } else {
            console.log(`    - ✅ AUCUNE MODIFICATION: Déjà dans readBy`);
            return message;
          }
        });

        console.log(`🔄 MARK-READ-DEBUG: Messages modifiés: ${messagesModified}/${discussion.messages.length}`);
        
        if (messagesModified > 0) {
          updates.messages = updatedMessages;
          needsUpdate = true;
          console.log('🔥 MARK-READ-DEBUG: Mise à jour des messages nécessaire');
        } else {
          console.log('✅ MARK-READ-DEBUG: Aucun message à modifier');
        }
      } else {
        console.log('📭 MARK-READ-DEBUG: Aucun message dans la discussion');
      }

      console.log('🔄 MARK-READ-DEBUG: needsUpdate:', needsUpdate);
      console.log('🔄 MARK-READ-DEBUG: Updates à appliquer:', updates);

      if (needsUpdate) {
        console.log('🔥 MARK-READ-DEBUG: *** MISE À JOUR FIREBASE ***');
        
        await updateDoc(doc(db, 'discussions', discussion.id), updates);
        
        console.log('✅ MARK-READ-DEBUG: Mise à jour Firebase réussie');
        
        // ✅ DÉCLENCHER DES ÉVÉNEMENTS pour rafraîchir les compteurs
        console.log('🔄 MARK-READ-DEBUG: Déclenchement des événements de sync...');
        
        eventBus.emit('forceRefreshNotificationCounts', {
          type: 'message', 
          discussionId: discussion.id,
          userId: auth.currentUser.uid
        });
        
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts', {
          detail: { 
            type: 'message', 
            discussionId: discussion.id,
            userId: auth.currentUser.uid,
            timestamp: Date.now()
          }
        }));
        
        console.log('✅ MARK-READ-DEBUG: Événements déclenchés');
        
        // ✅ FORCER UNE MISE À JOUR IMMÉDIATE DU STATE LOCAL
        setTimeout(() => {
          console.log('🔄 MARK-READ-DEBUG: Mise à jour différée du state local...');
          // Le onSnapshot devrait se déclencher automatiquement
        }, 100);
        
      } else {
        console.log('⚪ MARK-READ-DEBUG: Aucune mise à jour nécessaire');
      }
      
      console.log('🏁 MARK-READ-DEBUG: *** FIN MARQUAGE COMME LU ***');
      
    } catch (error) {
      console.error('❌ MARK-READ-DEBUG: *** ERREUR ***');
      console.error('❌ MARK-READ-DEBUG:', error);
    }
  };

  // Obtenir le rôle d'un utilisateur
  const getUserRole = (userId, userEmail) => {
    if (userId && userRoles[userId]?.role) {
      return userRoles[userId].role;
    }
    if (userEmail && userRoles[userEmail]?.role) {
      return userRoles[userEmail].role;
    }
    return 'Client';
  };

  // Normaliser le nom du rôle
  const normalizeRoleName = (role) => {
    if (!role) return 'Client';
    
    const roleLower = role.toLowerCase();
    
    if (roleLower === 'superadmin' || roleLower === 'super-admin' || roleLower === 'admin') {
      return 'Super Admin';
    }
    if (roleLower === 'projectmanager' || roleLower === 'project-manager' || roleLower === 'chef de projet') {
      return 'Chef de Projet';
    }
    if (roleLower === 'charge-affaire' || roleLower === 'chargé d\'affaire' || roleLower === 'chargeaffaire') {
      return 'Chargé d\'Affaire';  
    }
    if (roleLower === 'client' || roleLower === 'customer') {
      return 'Client';
    }
    
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Obtenir les participants uniques pour discussions
  const getUniqueParticipants = (discussion) => {
    const participantSet = new Set();
    const participants = [];
    
    if (discussion.participantNames) {
      Object.entries(discussion.participantNames).forEach(([userId, name]) => {
        if (!participantSet.has(userId)) {
          participantSet.add(userId);
          const roleName = normalizeRoleName(getUserRole(userId));
          participants.push({
            id: userId,
            name: name,
            role: roleName,
            colors: getColorsByRole(roleName)
          });
        }
      });
    }
    
    if (discussion.messages) {
      discussion.messages.forEach(message => {
        if (message.createdBy && !participantSet.has(message.createdBy)) {
          participantSet.add(message.createdBy);
          const roleName = normalizeRoleName(getUserRole(message.createdBy, message.createdByEmail));
          participants.push({
            id: message.createdBy,
            email: message.createdByEmail,
            name: roleName,
            colors: getColorsByRole(roleName)
          });
        }
      });
    }
    
    return participants;
  };

  // Composant Modal pour le nouveau Planning
  const PlanningModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full h-full max-w-none max-h-none overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
          <h2 className="text-xl font-bold text-gray-900">📅 Planning des interventions</h2>
          <button 
            onClick={() => setShowPlanning(false)}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="h-full overflow-y-auto">
          <NewClientCalendar />
        </div>
      </div>
    </div>
  );

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-gray-500 bg-gray-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'completed': return <CheckCircle2 className="w-5 h-5" />;
      case 'in_progress': return <Clock className="w-5 h-5" />;
      case 'pending': return <Clock className="w-5 h-5 opacity-50" />;
      default: return <Clock className="w-5 h-5 opacity-50" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // SIMPLIFIÉ: Si dossierId spécifique mais dossier non trouvé
  if (dossierId && !dossierData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Dossier non trouvé</h2>
          <p className="text-gray-600 mb-4">Le dossier demandé n'existe pas ou vous n'y avez pas accès.</p>
          {isMultiProject && (
            <button
              onClick={handleBackToProjects}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
            >
              ← Retour à mes dossiers
            </button>
          )}
        </div>
      </div>
    );
  }

  // IMPORTANT: Ce composant n'affiche JAMAIS de sélection de projets
  // La sélection est gérée par MultiDashboardClient.js
  if (!dossierId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Aucun dossier sélectionné</h2>
          <p className="text-gray-600 mb-4">Veuillez sélectionner un dossier pour continuer.</p>
          {isMultiProject && (
            <button
              onClick={handleBackToProjects}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
            >
              ← Voir mes dossiers
            </button>
          )}
        </div>
      </div>
    );
  }

  // Si on est sur une sous-route, afficher seulement l'Outlet
  if (isOnSubRoute) {
    console.log("🔄 Affichage de la sous-route:", location.pathname);
    return <Outlet />;
  }

  // DASHBOARD PRINCIPAL - Toujours pour un projet spécifique
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec lien retour si multi-projets - CORRIGÉ */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {isMultiProject && (
                <button
                  onClick={handleBackToProjects}
                  className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  title="Retourner à la sélection de dossiers"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Mes {userProjects.length} dossiers</span>
                </button>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{dossierData.titre || dossierData.dossierTitre || dossierData.nomClient}</h1>
                <p className="text-gray-600 mt-1">{dossierData.adresseClient || dossierData.adresse}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Type</div>
                <div className="font-semibold text-lg text-blue-600 capitalize">
                  {dossierData.typeClient || dossierData.type}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Raccordement</div>
                <div className="font-semibold text-lg text-green-600 capitalize">
                  {dossierData.raccordement}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation principale */}
        <div className="flex space-x-4 mb-8">
          <Link
            to={`/client/${dossierId}/documents`}
            className="flex-1 bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow flex items-center justify-center space-x-3"
          >
            <Download className="h-6 w-6 text-purple-600" />
            <span className="font-medium text-gray-900">Téléchargements</span>
          </Link>

          <Link
            to={`/admin-clients/${dossierId}/preferences`}
            className="flex-1 bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow flex items-center justify-center space-x-3"
          >
            <Settings className="h-6 w-6 text-gray-600" />
            <span className="font-medium text-gray-900">Paramètres</span>
          </Link>
        </div>

        {/* Barre de progression et indicateurs compacts */}
        <div className="mb-8">
          {/* Barre de progression */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Avancement</p>
                <p className="text-2xl font-semibold text-gray-900">{dossierData.progressGlobal || 0}%</p>
              </div>
            </div>
            <div className="mt-4">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${dossierData.progressGlobal || 0}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Indicateurs compacts sur une ligne */}
          <div className="flex space-x-4">
            <button
              onClick={() => {
                console.log("🗓️ Ouverture du nouveau planning");
                setShowPlanning(true);
              }}
              className="flex-1 bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow flex items-center justify-center space-x-3"
            >
              <Calendar className="h-6 w-6 text-blue-600" />
              <span className="font-medium text-gray-900">Planning des interventions</span>
            </button>

            <button
              onClick={() => {
                console.log("🔔 Ouverture de la messagerie");
                setShowNotifications(true);
              }}
              className="flex-1 bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow flex items-center justify-center space-x-3 relative"
            >
              <MessageSquare className="h-6 w-6 text-orange-600" />
              <span className="font-medium text-gray-900">
                Messagerie {messagesCount > 0 && `(${messagesCount})`}
              </span>
              {messagesCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
                  {messagesCount > 99 ? '99+' : messagesCount}
                </div>
              )}
            </button>

            <Link
              to={`/client/${dossierId}/documents`}
              className="flex-1 bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow flex items-center justify-center space-x-3"
            >
              <FileText className="h-6 w-6 text-purple-600" />
              <span className="font-medium text-gray-900">Documents</span>
            </Link>
          </div>
        </div>

        {/* Timeline moderne */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-gray-900">Suivi de votre dossier</h2>
            <div className="text-sm text-gray-500">
              Étape {dossierData.etapeActuelle} sur {dossierData.phases?.length || 8}
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            
            {dossierData.phases?.map((phase, index) => {
              const isActive = phase.statut === 'in_progress';
              const isCompleted = phase.statut === 'completed';
              
              return (
                <div key={phase.id} className="relative flex items-start mb-8 last:mb-0">
                  <div className={`
                    relative z-10 flex items-center justify-center w-16 h-16 rounded-full border-4 
                    ${isCompleted ? 'bg-green-100 border-green-500' : 
                      isActive ? 'bg-blue-100 border-blue-500 ring-4 ring-blue-100' : 
                      'bg-gray-100 border-gray-300'}
                  `}>
                    {getStatusIcon(phase.statut)}
                  </div>
                  
                  <div className="ml-6 flex-1">
                    <div className={`
                      p-6 rounded-lg border transition-all duration-200
                      ${isActive ? 'bg-blue-50 border-blue-200 shadow-md' : 
                        isCompleted ? 'bg-green-50 border-green-200' : 
                        'bg-gray-50 border-gray-200'}
                    `}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`font-semibold ${isCompleted ? 'text-green-800' : isActive ? 'text-blue-800' : 'text-gray-600'}`}>
                          {phase.nom}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(phase.statut)}`}>
                          {phase.statut === 'completed' ? 'Terminé' : 
                           phase.statut === 'in_progress' ? 'En cours' : 'À venir'}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-3">{phase.description}</p>
                      
                      {phase.dateFinition && (
                        <div className="text-xs text-green-600 font-medium">
                          Terminé le {phase.dateFinition.toLocaleDateString('fr-FR')}
                        </div>
                      )}
                      
                      {phase.dateDebut && phase.statut === 'in_progress' && (
                        <div className="text-xs text-blue-600 font-medium">
                          Démarré le {phase.dateDebut.toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modales */}
      {showNotifications && (
        <MessageModal
          isOpen={showNotifications}
          onClose={() => {
            console.log("🔔 Fermeture de la messagerie");
            setShowNotifications(false);
          }}
          discussionData={discussionData}
          userRoles={userRoles}
          formatDate={formatDate}
          getUserRole={getUserRole}
          normalizeRoleName={normalizeRoleName}
          getColorsByRole={getColorsByRole}
          getUniqueParticipants={getUniqueParticipants}
          markMessagesAsRead={markMessagesAsRead}
        />
      )}
      
      {showPlanning && <PlanningModal />}
    </div>
  );
};

// Composant MessageModal adapté pour discussions
const MessageModal = React.memo(({ 
 isOpen, 
 onClose, 
 discussionData, 
 userRoles, 
 formatDate, 
 getUserRole, 
 normalizeRoleName, 
 getColorsByRole, 
 getUniqueParticipants, 
 markMessagesAsRead 
}) => {
 const [newMessage, setNewMessage] = useState('');
 const [sending, setSending] = useState(false);
 const [selectedFile, setSelectedFile] = useState(null);
 const [uploading, setUploading] = useState(false);
 const messagesEndRef = useRef(null);
 const fileInputRef = useRef(null);

 const auth = getAuth();
 
 useEffect(() => {
   if (messagesEndRef.current && discussionData?.messages) {
     messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
   }
 }, [discussionData?.messages?.length]);

 // ✅ CORRECTION : Appel automatique de markMessagesAsRead quand modal s'ouvre
 useEffect(() => {
   console.log('🔄 MODAL-DEBUG: useEffect déclenché');
   console.log('🔄 MODAL-DEBUG: isOpen:', isOpen);
   console.log('🔄 MODAL-DEBUG: discussionData:', discussionData);
   
   if (discussionData && isOpen) {
     console.log('🔄 MODAL-DEBUG: Conditions remplies - appel markMessagesAsRead');
     markMessagesAsRead(discussionData);
   } else {
     console.log('🔄 MODAL-DEBUG: Conditions non remplies pour markMessagesAsRead');
   }
 }, [discussionData?.id, isOpen, markMessagesAsRead]);

 const uploadFileToStorage = async (file) => {
   try {
     setUploading(true);
     const fileName = `${Date.now()}_${file.name}`;
     const filePath = `attachments/${auth.currentUser.uid}/${fileName}`;
     const storageRef = ref(storage, filePath);
     const uploadResult = await uploadBytes(storageRef, file);
     const fileUrl = await getDownloadURL(uploadResult.ref);
     
     return {
       name: file.name,
       url: fileUrl,
       size: file.size,
       type: file.type
     };
   } catch (error) {
     console.error("❌ Erreur upload:", error);
     alert("Erreur lors de l'upload du fichier: " + error.message);
     return null;
   } finally {
     setUploading(false);
   }
 };

 const handleSendMessage = async (e) => {
   e.preventDefault();
   
   if ((!newMessage.trim() && !selectedFile) || sending || !discussionData) {
     return;
   }
   
   if (!auth.currentUser) {
     alert("Erreur: Vous devez être connecté pour envoyer un message");
     return;
   }
   
   try {
     setSending(true);
     
     let attachment = null;
     if (selectedFile) {
       attachment = await uploadFileToStorage(selectedFile);
       if (!attachment) return;
     }
     
     const messageId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random()}`;
     const newMessageObj = {
       id: messageId,
       text: newMessage.trim() || `Fichier: ${selectedFile?.name}`,
       createdBy: auth.currentUser.uid,
       createdByEmail: auth.currentUser.email,
       createdAt: Timestamp.now(),
       readBy: [auth.currentUser.uid],
       ...(attachment && { attachment })
     };

     const docRef = doc(db, 'discussions', discussionData.id);
     
     await updateDoc(docRef, {
       messages: arrayUnion(newMessageObj),
       updatedAt: Timestamp.now(),
       lastMessage: newMessageObj.text
     });
     
     setNewMessage('');
     setSelectedFile(null);
     
     setTimeout(() => {
       if (messagesEndRef.current) {
         messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
       }
     }, 200);
     
   } catch (error) {
     console.error('❌ ERREUR lors de l\'envoi du message:', error);
     alert(`Erreur lors de l'envoi: ${error.message}`);
   } finally {
     setSending(false);
   }
 };

 if (!isOpen || !discussionData) {
   return null;
 }

 return (
   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
     <div className="bg-white rounded-lg max-w-4xl w-full mx-4 h-5/6 flex flex-col">
       <div className="flex items-center justify-between p-6 border-b bg-gray-50">
         <div>
           <h2 className="text-xl font-bold text-gray-900">{discussionData.dossierTitre || discussionData.title}</h2>
           <div className="flex items-center space-x-2 mt-2">
             <Users className="w-4 h-4 text-gray-400" />
             <span className="text-sm text-gray-500">
               {discussionData.participants?.length || 0} participants
             </span>
           </div>
         </div>
         <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
           <X className="w-6 h-6" />
         </button>
       </div>

       <div className="flex-1 overflow-y-auto p-6 space-y-4">
         {discussionData.messages && discussionData.messages.length > 0 ? (
           discussionData.messages.map((message, index) => {
             const isCurrentUser = message.createdBy === auth.currentUser?.uid;
             const isSystemMessage = message.system === true;
             const roleName = isSystemMessage ? 'Système' : normalizeRoleName(getUserRole(message.createdBy, message.createdByEmail));
             const colors = getColorsByRole(roleName);
             
             if (isSystemMessage) {
               return (
                 <div key={`${message.id || index}-${message.createdAt?.seconds || Date.now()}`} className="text-center">
                   <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg inline-block text-sm">
                     {message.text}
                   </div>
                 </div>
               );
             }

             return (
               <div key={`${message.id || index}-${message.createdAt?.seconds || Date.now()}`} className={`flex items-start space-x-3 ${isCurrentUser ? 'justify-end' : ''}`}>
                 {!isCurrentUser && (
                   <div className={`w-8 h-8 rounded-full ${colors.avatar} flex items-center justify-center text-white text-xs`}>
                     {roleName.charAt(0).toUpperCase()}
                   </div>
                 )}
                 <div className="flex-1 max-w-md">
                   <div className={`${isCurrentUser ? 'bg-blue-500 text-white' : colors.bubble} p-3 rounded-lg border`}>
                     <div className={`flex items-center justify-between text-xs ${isCurrentUser ? 'text-blue-100' : 'text-gray-500'} mb-1`}>
                       <span>
                         {roleName} • {formatDate(message.createdAt)}
                       </span>
                     </div>
                     <p className={`text-sm ${isCurrentUser ? 'text-white' : 'text-gray-700'}`} style={{ whiteSpace: 'pre-line' }}>
                       {message.text}
                     </p>
                     
                     {message.attachment && (
                       <div className={`mt-2 p-2 rounded ${isCurrentUser ? 'bg-blue-400' : 'bg-white'} border`}>
                         <div className="flex items-center space-x-2">
                           <Paperclip className="w-4 h-4" />
                           <a 
                             href={message.attachment.url} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className={`text-sm underline ${isCurrentUser ? 'text-blue-100' : 'text-blue-600'}`}
                           >
                             {message.attachment.name}
                           </a>
                         </div>
                       </div>
                     )}
                   </div>
                 </div>
                 {isCurrentUser && (
                   <div className={`w-8 h-8 rounded-full ${colors.avatar} flex items-center justify-center text-white text-xs`}>
                     {roleName.charAt(0).toUpperCase()}
                   </div>
                 )}
               </div>
             );
           })
         ) : (
           <div className="text-center py-4 text-gray-500">
             Aucun message pour le moment
           </div>
         )}
         <div ref={messagesEndRef} />
       </div>

       <form onSubmit={handleSendMessage} className="p-4 border-t bg-gray-50">
         {selectedFile && (
           <div className="mb-2 p-2 bg-blue-50 rounded flex items-center justify-between">
             <div className="flex items-center space-x-2">
               <Paperclip className="w-4 h-4 text-blue-600" />
               <span className="text-sm text-blue-700">📎 {selectedFile.name}</span>
             </div>
             <button
               type="button"
               onClick={() => setSelectedFile(null)}
               className="text-blue-700 hover:text-blue-900"
             >
               <X className="w-4 h-4" />
             </button>
           </div>
         )}
         
         <div className="flex items-end space-x-2">
           <input
             ref={fileInputRef}
             type="file"
             onChange={(e) => setSelectedFile(e.target.files[0])}
             className="hidden"
             accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.csv"
           />
           
           <button
             type="button"
             onClick={() => fileInputRef.current?.click()}
             disabled={uploading}
             className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
           >
             <Paperclip className="w-5 h-5" />
           </button>
           
           <div className="flex-1">
             <textarea
               value={newMessage}
               onChange={(e) => setNewMessage(e.target.value)}
               placeholder="Tapez votre message..."
               className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
               rows="2"
               disabled={sending || uploading}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' && !e.shiftKey) {
                   e.preventDefault();
                   handleSendMessage(e);
                 }
               }}
             />
           </div>
           
           <button
             type="submit"
             disabled={(!newMessage.trim() && !selectedFile) || sending || uploading}
             className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
           >
             {sending || uploading ? (
               <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
             ) : (
               <Send className="w-5 h-5" />
             )}
           </button>
         </div>
       </form>
     </div>
   </div>
 );
});

export default ClientDashboard;